import { LitElement, html } from 'lit'

/**
 * Componente reutilizável de seleção de período (mês/ano)
 * Permite navegar entre meses usando botões de seta (anterior/próximo)
 * com wrap-around automático (janeiro→dezembro e dezembro→janeiro)
 *
 * Emite evento 'periodo-changed' com { mes, ano } a cada navegação
 * Utiliza tokens CSS do Design System para cores e estilos visuais
 *
 * Uso:
 *   <seletor-periodo
 *     .mes="${this.mesAtual}"
 *     .ano="${this.anoAtual}"
 *     @periodo-changed="${this._onPeriodoChanged}"
 *   ></seletor-periodo>
 *
 * @element seletor-periodo
 */
class SeletorPeriodo extends LitElement {
  static properties = {
    /** Mês exibido (0-11, onde 0=janeiro e 11=dezembro) */
    mes: { type: Number },
    /** Ano exibido (4 dígitos, ex: 2025) */
    ano: { type: Number }
  }

  /**
   * Desabilita Shadow DOM para utilizar classes Bulma CSS globais
   * @returns {this} O próprio elemento como render root (light DOM)
   */
  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    const hoje = new Date()
    this.mes = hoje.getMonth()
    this.ano = hoje.getFullYear()
  }

  /**
   * Decrementa o mês exibido com wrap-around
   * Quando o mês é janeiro (0), volta para dezembro (11) do ano anterior
   * Dispara evento periodo-changed após a atualização
   * @private
   */
  _decrementar () {
    if (this.mes === 0) {
      this.mes = 11
      this.ano--
    } else {
      this.mes--
    }
    this._dispararEvento()
  }

  /**
   * Incrementa o mês exibido com wrap-around
   * Quando o mês é dezembro (11), avança para janeiro (0) do próximo ano
   * Dispara evento periodo-changed após a atualização
   * @private
   */
  _incrementar () {
    if (this.mes === 11) {
      this.mes = 0
      this.ano++
    } else {
      this.mes++
    }
    this._dispararEvento()
  }

  /**
   * Formata o display do período no formato "nomeMês de ano"
   * Exemplo: "janeiro de 2025", "dezembro de 2024"
   * @returns {string} Display formatado com nome do mês em português minúsculo + "de" + ano
   * @private
   */
  _formatarDisplay () {
    const nomes = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
    ]
    return `${nomes[this.mes]} de ${this.ano}`
  }

  /**
   * Dispara CustomEvent 'periodo-changed' com o período atual
   * O evento borbulha pelo DOM e atravessa Shadow DOM boundaries (composed)
   * @fires periodo-changed
   * @private
   */
  _dispararEvento () {
    this.dispatchEvent(new CustomEvent('periodo-changed', {
      bubbles: true,
      composed: true,
      detail: { mes: this.mes, ano: this.ano }
    }))
  }

  /**
   * Renderiza o seletor de período com botões de navegação e display central
   * Layout: [ ◀ ] [ mês de ano ] [ ▶ ]
   * @returns {import('lit').TemplateResult} Template HTML do componente
   */
  render () {
    return html`
      <div class="is-flex is-align-items-center is-justify-content-center" style="gap: 0.75rem;">
        <button
          class="button is-small"
          data-cy="btn-mes-anterior"
          @click="${this._decrementar}"
          style="
            background-color: var(--bg-surface-secondary, #1D2740);
            border-color: var(--border, #26304A);
            color: var(--text-primary, #F4F7FF);
            border-radius: var(--radius-button, 14px);
            cursor: pointer;
          "
        >
          <span class="icon is-small">
            <i class="fas fa-chevron-left"></i>
          </span>
        </button>

        <span
          data-cy="filter-periodo-display"
          style="
            color: var(--text-primary, #F4F7FF);
            font-weight: 600;
            font-size: 1rem;
            min-width: 160px;
            text-align: center;
          "
        >${this._formatarDisplay()}</span>

        <button
          class="button is-small"
          data-cy="btn-mes-proximo"
          @click="${this._incrementar}"
          style="
            background-color: var(--bg-surface-secondary, #1D2740);
            border-color: var(--border, #26304A);
            color: var(--text-primary, #F4F7FF);
            border-radius: var(--radius-button, 14px);
            cursor: pointer;
          "
        >
          <span class="icon is-small">
            <i class="fas fa-chevron-right"></i>
          </span>
        </button>
      </div>
    `
  }
}

customElements.define('seletor-periodo', SeletorPeriodo)
